import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_STAMPS = 72;
const MIN_DISTANCE = 120;
const STAMP_SIZE = 240;

const getGraffitiColor = () => {
  const color = getComputedStyle(document.documentElement)
    .getPropertyValue('--active-graffiti-color')
    .trim();

  return color || '#f47a1f';
};

const GraffitiCanvas: React.FC = () => {
  const location = useLocation();
  const layerRef = useRef<HTMLDivElement>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer) {
      return;
    }

    layer.replaceChildren();
    lastPointRef.current = null;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') {
        return;
      }

      const layer = layerRef.current;
      if (!layer) {
        return;
      }

      const point = {
        x: event.clientX,
        y: event.clientY,
      };
      const lastPoint = lastPointRef.current;

      if (lastPoint) {
        const distance = Math.hypot(point.x - lastPoint.x, point.y - lastPoint.y);
        if (distance < MIN_DISTANCE) {
          return;
        }
      }

      lastPointRef.current = point;

      const stamp = document.createElement('div');
      const x = point.x - STAMP_SIZE / 2 + (Math.random() - 0.5) * 42;
      const y = point.y - STAMP_SIZE / 2 + (Math.random() - 0.5) * 42;
      const rotation = -18 + Math.random() * 36;
      const color = getGraffitiColor();
      const innerCloud = document.createElement('div');
      const outerCloud = document.createElement('div');

      stamp.className = 'graffiti-spray';
      stamp.style.left = `${x}px`;
      stamp.style.top = `${y}px`;
      stamp.style.transform = `rotate(${rotation}deg)`;

      outerCloud.className = 'graffiti-spray-cloud graffiti-spray-cloud-outer';
      outerCloud.style.background = `radial-gradient(circle, ${color}33 0%, ${color}18 38%, transparent 74%)`;
      outerCloud.style.transform = `translate(${(Math.random() - 0.5) * 18}px, ${(Math.random() - 0.5) * 18}px) scale(${1 + Math.random() * 0.2})`;

      innerCloud.className = 'graffiti-spray-cloud graffiti-spray-cloud-inner';
      innerCloud.style.background = `radial-gradient(circle, ${color}4d 0%, ${color}2a 28%, transparent 62%)`;
      innerCloud.style.transform = `translate(${(Math.random() - 0.5) * 26}px, ${(Math.random() - 0.5) * 26}px) scale(${0.82 + Math.random() * 0.18})`;

      stamp.appendChild(outerCloud);
      stamp.appendChild(innerCloud);

      layer.appendChild(stamp);

      while (layer.children.length > MAX_STAMPS) {
        layer.removeChild(layer.firstElementChild as Node);
      }
    };

    const handlePointerLeave = () => {
      lastPointRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, []);

  return <div className="graffiti-layer" ref={layerRef} aria-hidden="true" />;
};

export default GraffitiCanvas;
